import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import { Dificultad, Modalidad, Pregunta, TipoPregunta } from '../types';

type FeedbackType = 'bug' | 'suggestion';

const BUG_OPTIONS = [
  'Error en alguna respuesta',
  'Error en la pagina web',
  'Error en el Nivel 1',
  'Error en el Nivel 2',
  'Error en el Nivel 3',
  'Error en el Nivel 4',
  'Error con la cuenta',
  'Otro',
];

const SUGGESTION_OPTIONS = [
  'Nuevo nivel',
  'Anadir pregunta',
  'Personaje',
  'Cancion',
  'Nueva modalidad',
  'Corregir respuesta',
  'Otro',
];

const BASE_TOOLTIP = 'Desde el equipo de Main Gamer, valoramos vuestros comentarios y, gracias a vuestra colaboración, podemos mejorar cada día. Podéis indicarnos los errores que hayáis detectado o las funcionalidades que os gustaría ver implementadas en vuestro quiz de videojuegos favorito.';
const BUG_TOOLTIP = 'Lamentamos que hayas tenido inconvenientes durante tu experiencia en Main Gamer. Te agradecemos que nos describas brevemente el problema para que el equipo pueda solucionarlo lo antes posible.';
const SUGGESTION_TOOLTIP = '¿Te gustaría añadir una nueva modalidad? ¿O que tus preguntas, personajes favoritos, canciones de videojuegos y demás estén en Main Gamer? Ayúdanos a ir más allá: escuchamos a nuestra comunidad de gamers porque aquí tú eres el jugador principal.';

interface GameFeedbackWidgetProps {
  nickname?: string;
  modalidad?: Modalidad;
  dificultad?: Dificultad;
  nivelActual?: number;
  preguntaActual?: Pregunta;
  variant?: 'game' | 'header';
}

const GameFeedbackWidget: React.FC<GameFeedbackWidgetProps> = ({
  nickname,
  modalidad,
  dificultad,
  nivelActual,
  preguntaActual,
  variant = 'game',
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<FeedbackType | null>(null);
  const [categoria, setCategoria] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [thanksMessage, setThanksMessage] = useState('');
  const [hoveredMenuOption, setHoveredMenuOption] = useState<FeedbackType | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const [clickStep, setClickStep] = useState(0); // 0 = closed, 1 = explanation, 2 = menu
  const [isMobile] = useState(() => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));

  useEffect(() => {
    if (!selectedType) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedType]);

  useEffect(() => {
    if (!menuOpen && clickStep !== 1) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
        setClickStep(0);
      }
    };

    const handleScrollOrSwipe = () => {
      if (isMobile) {
        setMenuOpen(false);
        setClickStep(0);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    window.addEventListener('scroll', handleScrollOrSwipe, { passive: true });
    window.addEventListener('touchmove', handleScrollOrSwipe, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrSwipe);
      window.removeEventListener('touchmove', handleScrollOrSwipe);
    };
  }, [menuOpen, clickStep, isMobile]);

  const currentOptions = selectedType === 'bug' ? BUG_OPTIONS : SUGGESTION_OPTIONS;
  const currentTooltip = selectedType === 'bug' ? BUG_TOOLTIP : SUGGESTION_TOOLTIP;
  const currentIcon = selectedType === 'bug' ? '/logos/error.png' : '/logos/suggestion.png';
  const currentTitle = selectedType === 'bug' ? 'REPORTAR BUG' : 'APORTAR SUGERENCIA';

  const fallbackPregunta = useMemo<Pregunta>(() => ({
    id: 'general-feedback',
    nivel: 1,
    tipo: 'trivia',
    respuesta: '',
    info: '',
    texto: 'Feedback general',
    opciones: [],
  }), []);

  const preguntaContexto = preguntaActual || fallbackPregunta;
  const modalidadContexto = modalidad || 'classic';
  const dificultadContexto = dificultad || 'normal';
  const nivelContexto = nivelActual || 1;
  const nicknameContexto = (nickname || 'Invitado').trim() || 'Invitado';

  const preguntaInfo = useMemo(() => {
    if (preguntaContexto.tipo === 'trivia') return preguntaContexto.texto;
    if (preguntaContexto.tipo === 'portada') return preguntaContexto.respuesta;
    if (preguntaContexto.tipo === 'personaje') return preguntaContexto.respuesta;
    if (preguntaContexto.tipo === 'cancion') return preguntaContexto.respuesta;
    return '';
  }, [preguntaContexto]);

  const resetForm = () => {
    setCategoria('');
    setMensaje('');
    setError('');
    setSelectedType(null);
    setMenuOpen(false);
    setClickStep(0);
  };

  const handleOpenType = (type: FeedbackType) => {
    setSelectedType(type);
    setCategoria('');
    setMensaje('');
    setError('');
    setThanksMessage('');
    setHoveredMenuOption(null);
    setMenuOpen(false);
    setClickStep(0);
  };

  const handleSubmit = async () => {
    if (!selectedType) return;
    if (!categoria || !mensaje.trim()) {
      setError('Selecciona una categoria y escribe un mensaje antes de enviarlo.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(getApiUrl('/api/feedback'), {
        tipo: selectedType,
        categoria,
        mensaje,
        nickname: nicknameContexto,
        modalidad: modalidadContexto,
        dificultad: dificultadContexto,
        nivelActual: nivelContexto,
        preguntaTipo: (preguntaContexto.tipo || 'otro') as TipoPregunta | 'otro',
        preguntaId: preguntaContexto.id,
        preguntaInfo,
      });

      setThanksMessage(
        selectedType === 'bug'
          ? 'Lamentamos el problema que has detectado y te agradecemos el tiempo dedicado a ayudarnos a mejorar Main Gamer. Revisaremos tu informe lo antes posible.'
          : 'Gracias por ayudarnos a mejorar Main Gamer. Estudiaremos tu propuesta lo antes posible.'
      );
      setCategoria('');
      setMensaje('');
    } catch (submitError: any) {
      setError(submitError.response?.data?.error || 'No se pudo enviar el mensaje. Intentalo otra vez.');
    } finally {
      setLoading(false);
    }
  };

  const triggerButtonClass = variant === 'header'
    ? 'w-10 h-10 flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity'
    : 'w-14 h-14 rounded-2xl border border-white/15 bg-black/45 backdrop-blur-md flex items-center justify-center shadow-[0_12px_24px_rgba(0,0,0,0.28)] hover:border-gaming-cyan/60 hover:bg-black/65 transition-all';

  const triggerIconClass = variant === 'header' ? 'w-9 h-9 object-contain' : 'w-8 h-8 object-contain';

  const baseTooltipClass = variant === 'header'
    ? 'pointer-events-none absolute right-0 top-full mt-3 w-72 rounded-2xl border border-white/10 bg-black/90 px-4 py-3 text-sm text-white/80 opacity-0 transition-all group-hover:opacity-100'
    : 'pointer-events-none absolute left-0 top-full mt-3 w-72 rounded-2xl border border-white/10 bg-black/90 px-4 py-3 text-sm text-white/80 opacity-0 transition-all group-hover:opacity-100';

  const menuPanelClass = isMobile
    ? 'fixed top-[80px] left-1/2 -translate-x-1/2 w-[90%] max-w-md bg-[#0d1020]/95 border border-gaming-cyan/50 rounded-2xl p-4 z-50 shadow-[0_0_20px_rgba(0,255,255,0.25)] animate-fade-in backdrop-blur-xl'
    : (variant === 'header'
      ? 'absolute right-0 top-full mt-10 w-72 rounded-2xl border border-white/10 bg-[#0d1020]/95 backdrop-blur-xl p-3 shadow-[0_18px_32px_rgba(0,0,0,0.4)]'
      : 'absolute left-0 top-full mt-3 w-72 rounded-2xl border border-white/10 bg-[#0d1020]/95 backdrop-blur-xl p-3 shadow-[0_18px_32px_rgba(0,0,0,0.4)]');

  return (
    <>
      {(clickStep === 1 || (menuOpen && isMobile)) && (
        <div
          className="fixed inset-0 bg-black/70 z-40 transition-opacity duration-300 backdrop-blur-[2px]"
          onClick={() => {
            setClickStep(0);
            setMenuOpen(false);
          }}
          onTouchMove={() => {
            setClickStep(0);
            setMenuOpen(false);
          }}
        />
      )}
      <div className={variant === 'game' ? 'w-full flex justify-start mt-4 z-50 relative' : 'relative z-50'}>
        <div ref={menuRef} className="relative group">
          <button
            type="button"
            onClick={() => {
              if (isMobile) {
                if (clickStep === 0) {
                  setClickStep(1);
                  setMenuOpen(false);
                } else if (clickStep === 1) {
                  setClickStep(2);
                  setMenuOpen(true);
                } else {
                  setClickStep(0);
                  setMenuOpen(false);
                }
              } else {
                setMenuOpen((prev) => !prev);
              }
              setThanksMessage('');
            }}
            className={triggerButtonClass}
            aria-label="Abrir centro de feedback"
          >
            <img src="/logos/letter.png" alt="Centro de feedback" className={triggerIconClass} />
          </button>

          {!menuOpen && !isMobile && (
            <div className={baseTooltipClass}>
              <p className="font-main leading-relaxed">{BASE_TOOLTIP}</p>
            </div>
          )}

          {clickStep === 1 && isMobile && (
            <div className="fixed top-[80px] left-1/2 -translate-x-1/2 w-[90%] max-w-md bg-[#0d1020]/95 border border-gaming-cyan/50 rounded-2xl p-4 text-white text-sm z-50 text-center shadow-[0_0_20px_rgba(0,255,255,0.25)] animate-fade-in backdrop-blur-xl">
              <p className="font-main leading-relaxed mb-2 text-white/90">
                {BASE_TOOLTIP}
              </p>
              <p className="font-gaming text-gaming-cyan text-[11px] tracking-widest uppercase mt-2 select-none">
                Pulsa de nuevo el icono para ver las opciones bug / sugerencias
              </p>
            </div>
          )}

          {menuOpen && (
            <div className={menuPanelClass}>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => handleOpenType('bug')}
                  onMouseEnter={() => setHoveredMenuOption('bug')}
                  onMouseLeave={() => setHoveredMenuOption(null)}
                  className="w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left hover:bg-white/8 transition-colors"
                >
                  <img src="/logos/error.png" alt="Reportar bug" className="w-10 h-10 object-contain" />
                  <div>
                    <p className="font-gaming text-sm text-white tracking-wide">REPORTAR BUG</p>
                    <p className="font-main text-xs text-white/55">Ayudanos a detectar y corregir errores.</p>
                  </div>
                </button>
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => handleOpenType('suggestion')}
                  onMouseEnter={() => setHoveredMenuOption('suggestion')}
                  onMouseLeave={() => setHoveredMenuOption(null)}
                  className="w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left hover:bg-white/8 transition-colors"
                >
                  <img src="/logos/suggestion.png" alt="Aportar sugerencia" className="w-10 h-10 object-contain" />
                  <div>
                    <p className="font-gaming text-sm text-white tracking-wide">APORTAR SUGERENCIA</p>
                    <p className="font-main text-xs text-white/55">Propón mejoras, niveles, preguntas o ideas.</p>
                  </div>
                </button>
              </div>

              {hoveredMenuOption && (
                <div className="pointer-events-none absolute left-0 top-full mt-3 w-72 rounded-2xl border border-white/10 bg-black/90 px-4 py-3 text-sm text-white/80 z-20">
                  <p className="font-main leading-relaxed">
                    {hoveredMenuOption === 'bug' ? BUG_TOOLTIP : SUGGESTION_TOOLTIP}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {selectedType && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4" onClick={resetForm}>
          <div className="card-gaming w-full max-w-2xl max-h-[88vh] overflow-y-auto overscroll-contain !bg-[#0f1326] border border-white/20 shadow-[0_22px_48px_rgba(0,0,0,0.55)]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 mb-6">
              <div className="flex items-center gap-4 min-w-0">
                <div className="relative group shrink-0">
                  <img src={currentIcon} alt={currentTitle} className="w-14 h-14 object-contain" />
                  <div className="pointer-events-none absolute left-0 top-full mt-3 w-80 rounded-2xl border border-white/10 bg-black/90 px-4 py-3 text-sm text-white/80 opacity-0 transition-all group-hover:opacity-100 z-10 md:left-full md:top-1/2 md:mt-0 md:ml-3 md:-translate-y-1/2">
                    <p className="font-main leading-relaxed">{currentTooltip}</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-gaming text-gaming-cyan text-lg tracking-wider">{currentTitle}</h3>
                  <p className="font-main text-sm text-white/55 leading-relaxed">
                    Cuéntanoslo con el mayor detalle posible para que podamos revisarlo mejor.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={resetForm}
                className="text-2xl text-white/45 hover:text-white transition-colors"
                aria-label="Cerrar formulario de feedback"
              >
                ×
              </button>
            </div>

            {thanksMessage ? (
              <div className="space-y-5">
                <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-4">
                  <p className="font-main text-emerald-200 leading-relaxed">{thanksMessage}</p>
                </div>
                <div className="flex justify-end">
                  <button type="button" onClick={resetForm} className="btn-primary">
                    CERRAR
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                {error && (
                  <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-red-300 font-main text-sm">
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-main text-white/70 text-sm mb-2">Tipo de aviso</label>
                    <div className="rounded-xl border border-white/20 bg-[#151b31] px-4 py-3 text-white font-main text-sm">
                      {selectedType === 'bug' ? 'Reporte de error' : 'Sugerencia de la comunidad'}
                    </div>
                  </div>
                  <div>
                    <label className="block font-main text-white/70 text-sm mb-2">Categoria</label>
                    <select
                      value={categoria}
                      onChange={(e) => setCategoria(e.target.value)}
                      className="w-full rounded-xl border border-white/20 bg-[#151b31] px-4 py-3 text-white font-main focus:outline-none focus:border-gaming-cyan"
                    >
                      <option value="">Selecciona una opcion</option>
                      {currentOptions.map((option) => (
                        <option key={option} value={option} className="bg-[#111526] text-white">
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-xl border border-white/20 bg-[#151b31] px-4 py-3">
                    <p className="font-main text-white/45 text-xs mb-1">Jugador</p>
                    <p className="font-main text-white text-sm truncate">{nicknameContexto}</p>
                  </div>
                  <div className="rounded-xl border border-white/20 bg-[#151b31] px-4 py-3">
                    <p className="font-main text-white/45 text-xs mb-1">Modo</p>
                    <p className="font-main text-white text-sm uppercase">{variant === 'header' ? 'web' : modalidadContexto}</p>
                  </div>
                  <div className="rounded-xl border border-white/20 bg-[#151b31] px-4 py-3">
                    <p className="font-main text-white/45 text-xs mb-1">Contexto</p>
                    <p className="font-main text-white text-sm">{variant === 'header' ? 'Header principal' : `Nivel ${nivelContexto} · ${preguntaContexto.tipo}`}</p>
                  </div>
                </div>

                <div>
                  <label className="block font-main text-white/70 text-sm mb-2">
                    {selectedType === 'bug' ? 'Describe el problema' : 'Escribe tu sugerencia'}
                  </label>
                  <textarea
                    value={mensaje}
                    onChange={(e) => setMensaje(e.target.value)}
                    rows={7}
                    placeholder={selectedType === 'bug'
                      ? 'Explica brevemente que ha pasado, donde lo has visto y como podemos reproducirlo.'
                      : 'Cuentanos que te gustaria anadir, mejorar o ver en Main Gamer.'}
                    className="w-full rounded-2xl border border-white/20 bg-[#151b31] px-4 py-4 text-white font-main resize-y min-h-[180px] focus:outline-none focus:border-gaming-cyan"
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <p className="font-main text-xs text-white/45 leading-relaxed max-w-lg">
                    Guardaremos tu mensaje para revisarlo en el equipo de Main Gamer junto con el contexto de la partida actual.
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="font-main font-bold px-6 py-3 rounded-lg transition-all duration-200 active:scale-95 text-white text-sm"
                      style={{
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 55%, #b91c1c 100%)',
                        border: '1px solid rgba(248, 113, 113, 0.85)',
                        boxShadow: '0 0 14px rgba(239, 68, 68, 0.35), 0 0 26px rgba(220, 38, 38, 0.24), inset 0 1px 1px rgba(255, 255, 255, 0.2)',
                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.6)',
                      }}
                    >
                      CANCELAR
                    </button>
                    <button type="button" onClick={handleSubmit} disabled={loading} className="btn-primary text-sm disabled:opacity-60">
                      {loading ? 'ENVIANDO...' : 'ENVIAR'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default GameFeedbackWidget;
